// src/Login.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { signInWithEmailAndPassword } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { doc, getDoc, updateDoc, arrayRemove, runTransaction } from "firebase/firestore";
import { auth, db } from "./firebase";
import logo from "./assets/image/logo_web.png";
import Banner from './components/Banner';
import SplashScreen from "./SplashScreen";

export default function Login({ onLogin }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [useUsernameLogin, setUseUsernameLogin] = useState(false);
  const [username, setUsername] = useState("");
  const [ambiguousMatches, setAmbiguousMatches] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showSplash, setShowSplash] = useState(false);
  const [deviceLimit, setDeviceLimit] = useState(null);
  const [userDevices, setUserDevices] = useState([]);

  

  // Generate or retrieve device ID
  function getDeviceId() {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
  }

  // Get device info
  function getDeviceInfo() {
    const ua = navigator.userAgent;
    let type = 'desktop';
    let browser = 'Unknown';
    let os = 'Unknown';

    // Detect device type
    if (/mobile/i.test(ua)) type = 'mobile';
    else if (/tablet|ipad/i.test(ua)) type = 'tablet';

    // Detect browser
    if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Safari')) browser = 'Safari';
    else if (ua.includes('Edge')) browser = 'Edge';

    // Detect OS
    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iOS')) os = 'iOS';

    return {
      id: getDeviceId(),
      type,
      browser,
      os,
      name: `${browser} on ${os}`,
      addedAt: new Date(),
      lastLogin: new Date()
    };
  }

  async function handleDeviceLimit(userId, devices) {
    return new Promise((resolve) => {
      setDeviceLimit({
        userId,
        devices,
        onRemove: async (deviceId) => {
          try {
            const deviceToRemove = devices.find(d => d.id === deviceId);
            await updateDoc(doc(db, 'users', userId), {
              devices: arrayRemove(deviceToRemove)
            });
            setDeviceLimit(null);
            resolve(true);
          } catch (err) {
            console.error('Error removing device:', err);
            alert('Failed to remove device: ' + err.message);
            resolve(false);
          }
        },
        onCancel: () => {
          setDeviceLimit(null);
          resolve(false);
        }
      });
    });
  }

  async function handleLogin(e) {
    e.preventDefault();
    setError("");
    try {
      let authEmail = email;
      if (useUsernameLogin) {
        // If we already have ambiguous matches and user selected a company, use that
        if (ambiguousMatches.length > 1 && selectedCompanyId) {
          const match = ambiguousMatches.find(m => m.companyId === selectedCompanyId);
          if (!match) {
            setError('Please select a valid company to continue.');
            return;
          }
          authEmail = match.email;
        } else {
          // Resolve auth email from username via Cloud Function
          const functions = getFunctions();
          const resolveFn = httpsCallable(functions, 'resolveAuthEmailForUsername');
          const res = await resolveFn({ username });
          const data = res.data || {};
          if (data.email) {
            authEmail = data.email;
            setAmbiguousMatches([]);
            setSelectedCompanyId("");
          } else if (Array.isArray(data.matches) && data.matches.length > 1) {
            // Ambiguous across companies; prompt user to select company
            setAmbiguousMatches(data.matches);
            setError('Username exists in multiple companies. Please select your company.');
            return; // Wait for user to select company and resubmit
          } else {
            throw new Error(data.error || 'Unable to resolve username. Try email login.');
          }
        }
      }

      const userCred = await signInWithEmailAndPassword(auth, authEmail, password);
      
      // Check device limit
      const userDoc = await getDoc(doc(db, 'users', userCred.user.uid));
      if (!userDoc.exists()) {
        throw new Error('User document not found');
      }

      const userData = userDoc.data();
      
      // Check if company is locked
      if (userData.companyId) {
        const companyDoc = await getDoc(doc(db, 'companies', userData.companyId));
        if (companyDoc.exists()) {
          const companyData = companyDoc.data();
          if (companyData.subscriptionStatus === 'locked') {
            await auth.signOut();
            const deleteDate = companyData.deleteAt ? new Date(companyData.deleteAt).toLocaleDateString() : 'soon';
            throw new Error(`Your trial has expired. Your account is locked and will be deleted on ${deleteDate}. Please contact support or subscribe to reactivate.`);
          }
        }
      }

      // Device limit enforcement (atomic transaction)
      // Exception: testers@buildmychecks.co.uk bypasses device limit
      const DEVICE_LIMIT = 2;
      const EXEMPT_EMAILS = ['testers@buildmychecks.co.uk'];
      const userEmail = (userCred.user.email || '').toLowerCase();

      if (!EXEMPT_EMAILS.includes(userEmail)) {
        const currentDevice = getDeviceInfo();
        const userRef = doc(db, 'users', userCred.user.uid);

        const deviceResult = await runTransaction(db, async (transaction) => {
          const freshDoc = await transaction.get(userRef);
          if (!freshDoc.exists()) throw new Error('User document not found');

          const devices = freshDoc.data().devices || [];
          const existingIndex = devices.findIndex(d => d.id === currentDevice.id);

          if (existingIndex >= 0) {
            // Existing device — update lastLogin
            devices[existingIndex].lastLogin = new Date();
            transaction.update(userRef, { devices });
            return { action: 'updated' };
          } else if (devices.length >= DEVICE_LIMIT) {
            // Over limit — need user to remove a device first
            return { action: 'limit', devices };
          } else {
            // Under limit — add new device
            devices.push(currentDevice);
            transaction.update(userRef, { devices });
            return { action: 'added' };
          }
        });

        if (deviceResult.action === 'limit') {
          setUserDevices(deviceResult.devices);
          const shouldContinue = await handleDeviceLimit(userCred.user.uid, deviceResult.devices);

          if (!shouldContinue) {
            await auth.signOut();
            setError('Login cancelled. You must remove a device to continue.');
            return;
          }

          // After removal, add current device in a new transaction
          await runTransaction(db, async (transaction) => {
            const freshDoc = await transaction.get(userRef);
            const updatedDevices = freshDoc.data().devices || [];
            if (updatedDevices.length >= DEVICE_LIMIT) {
              throw new Error('Device limit still reached. Please try again.');
            }
            updatedDevices.push(currentDevice);
            transaction.update(userRef, { devices: updatedDevices });
          });
        }
      } else {
        // Exempt user — just register device without limit check
        const currentDevice = getDeviceInfo();
        const devices = userData.devices || [];
        const existingIndex = devices.findIndex(d => d.id === currentDevice.id);
        if (existingIndex >= 0) {
          devices[existingIndex].lastLogin = new Date();
        } else {
          devices.push(currentDevice);
        }
        await updateDoc(doc(db, 'users', userCred.user.uid), { devices });
      }

      // ✅ Show splash screen before loading dashboard
      setShowSplash(true);

      // When splash finishes (after ~3.5s), call onLogin
      setTimeout(() => {
        onLogin(userCred.user);
      }, 3600);
    } catch (err) {
      // User-friendly error messages instead of raw Firebase errors
      switch (err.code) {
        case 'auth/user-not-found':
          setError(t('auth.errorNoAccountFound'));
          break;
        case 'auth/wrong-password':
          setError(t('auth.errorWrongPassword'));
          break;
        case 'auth/invalid-credential':
          setError(t('auth.errorInvalidCredentials'));
          break;
        case 'auth/invalid-email':
          setError(t('auth.errorInvalidEmailFormat'));
          break;
        case 'auth/user-disabled':
          setError(t('auth.errorAccountDisabled'));
          break;
        case 'auth/too-many-requests':
          setError(t('auth.errorTooManyRequests'));
          break;
        case 'auth/network-request-failed':
          setError(t('auth.errorNetworkFailed'));
          break;
        default:
          // For non-Firebase errors (e.g. device limit, trial expired), show original message
          setError(err.code ? t('auth.errorGenericLogin') : err.message);
      }
    }
  }

  function handleShowRegister() {
    navigate('/register');
  }

  // ✅ If splash is showing, render it instead of the login form
  if (showSplash) {
    return <SplashScreen onFinish={() => onLogin(auth.currentUser)} />;
  }

  // Device limit modal
  if (deviceLimit) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-200 dark:bg-gray-900 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full">
          <div className="border-b p-6 dark:border-gray-700">
            <h2 className="text-xl font-bold text-red-700 dark:text-red-500">{t('auth.deviceLimitTitle')}</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {t('auth.deviceLimitMessage')}
            </p>
          </div>

          <div className="p-6">
            <div className="space-y-3">
              {deviceLimit.devices.map((device) => (
                <div
                  key={device.id}
                  className="border rounded-lg p-4 flex items-start justify-between dark:border-gray-700"
                >
                  <div className="flex items-start gap-3 flex-1">
                    <div className="text-3xl">
                      {device.type === 'mobile' ? '📱' : device.type === 'tablet' ? '📲' : '💻'}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium dark:text-white">{device.name || t('auth.unknownDevice')}</h3>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        <div><strong>{t('auth.browser')}</strong> {device.browser || 'Unknown'}</div>
                        <div><strong>{t('auth.os')}</strong> {device.os || 'Unknown'}</div>
                        <div><strong>{t('auth.lastLogin')}</strong> {device.lastLogin ? new Date(device.lastLogin.seconds * 1000).toLocaleString() : 'Unknown'}</div>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => deviceLimit.onRemove(device.id)}
                    className="ml-4 px-3 py-1 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded border border-red-300 dark:border-red-800"
                  >
                    {t('auth.remove')}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t p-6 dark:border-gray-700">
            <button
              onClick={deviceLimit.onCancel}
              className="w-full bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 py-2 px-4 rounded font-medium dark:text-white"
            >
              {t('auth.cancelLogin')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black text-gray-50 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -left-32 -top-32 w-72 h-72 bg-red-600/30 blur-3xl rounded-full" />
        <div className="absolute right-0 top-10 w-96 h-96 bg-rose-500/20 blur-3xl rounded-full" />
        <div className="absolute -right-24 bottom-0 w-80 h-80 bg-amber-400/10 blur-3xl rounded-full" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-10 lg:py-14">
        <div className="flex items-center justify-between mb-10">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-sm text-gray-200 hover:text-white transition"
          >
            <span className="text-lg">←</span>
            {t('common.back')}
          </button>
          <Banner className="w-48 lg:w-56" />
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div className="hidden lg:block space-y-6">
            <div className="inline-flex items-center gap-3 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs uppercase tracking-[0.2em] text-gray-200">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              Secure workspace access
            </div>
            <h1 className="text-3xl lg:text-4xl font-bold text-white leading-tight">
              {t('auth.logBackIn')}
            </h1>
            <p className="text-gray-300 text-base leading-relaxed">
              {t('auth.loginDescription')}
            </p>
            <div className="flex items-center gap-4 text-sm text-gray-300">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-emerald-400" /> {t('auth.uptime')}
              </div>
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-sky-400" /> {t('auth.ssoReady')}
              </div>
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-amber-300" /> {t('auth.ukData')}
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 -m-4 bg-white/5 blur-xl rounded-3xl" />
            <div className="relative bg-gray-900/70 backdrop-blur-lg border border-white/10 rounded-3xl shadow-2xl p-8 lg:p-10">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <img src={logo} alt="BuildMyChecks Logo" className="w-12 h-12 object-contain" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-gray-400">BuildMyChecks</p>
                    <h2 className="text-xl font-semibold text-white">{t('nav.signIn')}</h2>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/')}
                  className="text-sm text-gray-300 hover:text-white transition"
                >
                  {t('nav.home')}
                </button>
              </div>

              <form onSubmit={handleLogin} className="space-y-4">
                <div className="flex items-center gap-3 text-sm text-gray-200">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="loginMethod"
                      value="email"
                      checked={!useUsernameLogin}
                      onChange={() => setUseUsernameLogin(false)}
                      className="accent-red-600"
                    />
                    {t('auth.emailOrUsername')}
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="loginMethod"
                      value="username"
                      checked={useUsernameLogin}
                      onChange={() => setUseUsernameLogin(true)}
                      className="accent-red-600"
                    />
                    {t('auth.username')}
                  </label>
                </div>

                {!useUsernameLogin ? (
                  <input
                    type="email"
                    placeholder={t('auth.workEmail')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                  />
                ) : (
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder={t('auth.usernamePlaceholder')}
                      value={username}
                      onChange={(e) => { setUsername(e.target.value.replace(/\s+/g, '')); setAmbiguousMatches([]); setSelectedCompanyId(""); }}
                      required
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                    />
                    {ambiguousMatches.length > 1 && (
                      <div className="text-left space-y-1">
                        <label className="block text-xs text-gray-300">{t('auth.selectCompany')}</label>
                        <select
                          value={selectedCompanyId}
                          onChange={(e) => setSelectedCompanyId(e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                        >
                          <option value="">{t('auth.chooseCompany')}</option>
                          {ambiguousMatches.map((m) => (
                            <option key={m.companyId} value={m.companyId}>{m.companyName}</option>
                          ))}
                        </select>
                        <p className="text-[11px] text-gray-400">{t('auth.usernameMultipleCompanies')}</p>
                      </div>
                    )}
                  </div>
                )}

                <input
                  type="password"
                  placeholder={t('auth.password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                />

                <button
                  type="submit"
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-xl font-semibold transition transform hover:-translate-y-[1px] shadow-lg shadow-red-900/30"
                >
                  {t('auth.logIn')}
                </button>
              </form>

              <div className="flex items-center justify-between mt-4 text-sm text-gray-300">
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="hover:text-white transition"
                >
                  {t('auth.forgotPassword')}
                </button>
                <button
                  type="button"
                  onClick={handleShowRegister}
                  className="text-red-300 hover:text-red-200 font-semibold transition"
                >
                  {t('auth.createAccount')}
                </button>
              </div>

              {error && (
                <p className="text-red-300 text-xs mt-3">{error}</p>
              )}

              <p className="text-[11px] text-gray-400 mt-6">{t('common.copyright', { year: new Date().getFullYear() })}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
